import { createFileRoute } from '@tanstack/react-router'
import { Header } from '@/components/Header'
import { useColors } from '@/lib/utils'
import { useThemeStore } from '@/stores/theme'
import { Bell, Moon, Shield, HelpCircle, Sun, Laptop } from 'lucide-react'

export const Route = createFileRoute('/_authenticated/settings')({
  component: SettingsPage,
})

function SettingsPage() {
  const { isDarkMode, themeMode, setThemeMode } = useThemeStore()
  const Colors = useColors(isDarkMode)

  const handleHelpCenter = () => {
    window.open('https://support.restronaut.ai/submit', '_blank')
  }

  return (
    <div className="flex-1">
      <Header title="Settings" />

      <div className="p-6 space-y-6">
        {/* Appearance Section */}
        <div 
          className="p-6 rounded-xl shadow-sm"
          style={{ 
            backgroundColor: Colors.background.paper,
            boxShadow: isDarkMode 
              ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
              : '0 2px 4px rgba(0, 0, 0, 0.05)',
          }}
        >
          <h3 className="text-lg font-inter-bold mb-4" style={{ color: Colors.text.primary }}>
            Appearance
          </h3>
          <p className="text-sm font-inter-regular mb-6" style={{ color: Colors.text.secondary }}>
            Choose how the application looks and feels
          </p>
          
          <div 
            className="flex p-1 rounded-lg"
            style={{ backgroundColor: Colors.background.paper }}
          >
            <button
              onClick={() => setThemeMode('light')}
              className={`flex items-center space-x-2 px-4 py-3 rounded-md flex-1 font-inter-medium transition-colors ${
                themeMode === 'light' ? 'shadow-sm' : ''
              }`}
              style={{ 
                backgroundColor: themeMode === 'light' ? Colors.primary.main + '20' : 'transparent',
                color: themeMode === 'light' ? Colors.primary.main : Colors.text.primary,
              }}
            >
              <Sun size={20} />
              <span>Light</span>
            </button>

            <button
              onClick={() => setThemeMode('dark')}
              className={`flex items-center space-x-2 px-4 py-3 rounded-md flex-1 font-inter-medium transition-colors ${
                themeMode === 'dark' ? 'shadow-sm' : ''
              }`}
              style={{ 
                backgroundColor: themeMode === 'dark' ? Colors.primary.main + '20' : 'transparent',
                color: themeMode === 'dark' ? Colors.primary.main : Colors.text.primary,
              }}
            >
              <Moon size={20} />
              <span>Dark</span>
            </button>

            <button
              onClick={() => setThemeMode('system')}
              className={`flex items-center space-x-2 px-4 py-3 rounded-md flex-1 font-inter-medium transition-colors ${
                themeMode === 'system' ? 'shadow-sm' : ''
              }`}
              style={{ 
                backgroundColor: themeMode === 'system' ? Colors.primary.main + '20' : 'transparent',
                color: themeMode === 'system' ? Colors.primary.main : Colors.text.primary,
              }}
            >
              <Laptop size={20} />
              <span>System</span>
            </button>
          </div>
        </div>

        {/* Notifications Section */}
        <div 
          className="p-6 rounded-xl shadow-sm"
          style={{ 
            backgroundColor: Colors.background.paper,
            boxShadow: isDarkMode 
              ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
              : '0 2px 4px rgba(0, 0, 0, 0.05)',
          }}
        >
          <h3 className="text-lg font-inter-bold mb-4" style={{ color: Colors.text.primary }}>
            Notifications
          </h3>
          
          <button 
            className="flex items-center justify-between w-full p-4 rounded-lg"
            style={{ backgroundColor: Colors.background.default }}
          >
            <div className="flex items-center space-x-3">
              <Bell size={20} color={Colors.text.primary} />
              <span className="font-inter-regular" style={{ color: Colors.text.primary }}>
                Push Notifications
              </span>
            </div>
          </button>
        </div>

        {/* Security Section */}
        <div 
          className="p-6 rounded-xl shadow-sm"
          style={{ 
            backgroundColor: Colors.background.paper,
            boxShadow: isDarkMode 
              ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
              : '0 2px 4px rgba(0, 0, 0, 0.05)',
          }}
        >
          <h3 className="text-lg font-inter-bold mb-4" style={{ color: Colors.text.primary }}>
            Security
          </h3>
          
          <button 
            className="flex items-center justify-between w-full p-4 rounded-lg"
            style={{ backgroundColor: Colors.background.default }}
          >
            <div className="flex items-center space-x-3">
              <Shield size={20} color={Colors.text.primary} />
              <span className="font-inter-regular" style={{ color: Colors.text.primary }}>
                Privacy Settings
              </span>
            </div>
          </button>
        </div>

        {/* Support Section */}
        <div 
          className="p-6 rounded-xl shadow-sm"
          style={{ 
            backgroundColor: Colors.background.paper,
            boxShadow: isDarkMode 
              ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
              : '0 2px 4px rgba(0, 0, 0, 0.05)',
          }}
        >
          <h3 className="text-lg font-inter-bold mb-4" style={{ color: Colors.text.primary }}>
            Support
          </h3>
          
          <button 
            onClick={handleHelpCenter}
            className="flex items-center justify-between w-full p-4 rounded-lg hover:bg-opacity-50 transition-colors"
            style={{ backgroundColor: Colors.background.default }}
          >
            <div className="flex items-center space-x-3">
              <HelpCircle size={20} color={Colors.text.primary} />
              <span className="font-inter-regular" style={{ color: Colors.text.primary }}>
                Help Center
              </span>
            </div>
          </button>
        </div>
      </div>
    </div>
  )
}